import { SoftDeleteEntityWithId } from "./structures";

export class EntityCollection<T extends SoftDeleteEntityWithId> {
    private _itemMap: Map<string, T> = new Map();

    public get allNotDeleted(): T[] {
        return Array.from(this._itemMap.values()).filter(x => !x.isDeleted);
    }

    public get all(): T[] {
        return Array.from(this._itemMap.values());
    }

    public isExist(id: string): boolean {
        const item = this._itemMap.get(id);
        return !!item && !item.isDeleted;
    }

    public upsertMany = (items: T[]) => {
        items.forEach(item => this._itemMap.set(item.id, item));
    }

    public isExistIgnoreDeleted(id: string): boolean {
        return this._itemMap.has(id);
    }

    public isDeleted(id: string): boolean {
        const item = this._itemMap.get(id);
        return !!item && item.isDeleted;
    }

    public mustGet = (id: string): T => {
        const item = this._itemMap.get(id);
        if (!item || item.isDeleted) {
            throw new Error(`Item with ID "${id}" not found`);
        }
        return item;
    }

    public getMany = (ids: string[]): T[] => {
        return ids
            .map(id => this.maybeGet(id))
            .filter((item): item is T => !!item);
    }

    public mustGetMany = (ids: string[]): T[] => {
        return ids.map(id => this.mustGet(id));
    }

    public maybeGetIgnoreDeleted = (id: string): T | undefined => {
        return this._itemMap.get(id);
    }

    public mustGetIgnoreDeleted = (id: string): T => {
        const item = this._itemMap.get(id);
        if (!item) {
            throw new Error(`Item with ID "${id}" not found`);
        }
        return item;
    }

    public maybeGet = (id: string): T | undefined => {
        const existingItem = this._itemMap.get(id);

        if (!!existingItem && !existingItem.isDeleted) {
            return existingItem;
        }

        return undefined;
    }

    public mustUndelete = (id: string): void => {
        const existingItem = this._itemMap.get(id);
        if (!existingItem) {
            throw new Error(`Item with ID "${id}" not found`);
        }
        existingItem.isDeleted = false;
    }

    public maybeUndelete = (id: string): boolean => {
        const existingItem = this._itemMap.get(id);
        if (!existingItem) {
            return false;
        }
        if (!existingItem.isDeleted) {
            return false;
        }
        existingItem.isDeleted = false;
        return true;
    }

    public mustDelete = (id: string): void => {
        const existingItem = this._itemMap.get(id);
        if (!existingItem) {
            throw new Error(`Item with ID "${id}" not found`);
        }
        existingItem.isDeleted = true;
    }

    public maybeDelete = (id: string): void => {
        const item = this._itemMap.get(id);
        if (item) {
            item.isDeleted = true;
        }
    }

    public filter = (predicate: (item: T) => boolean): T[] => {
        return this.allNotDeleted.filter(predicate);
    }

    public find = (predicate: (item: T) => boolean): T | undefined => {
        return this.allNotDeleted.find(predicate);
    }

    public forEach = (callback: (item: T) => void): void => {
        this.allNotDeleted.forEach(callback);
    }

    public some = (predicate: (item: T) => boolean): boolean => {
        return this.allNotDeleted.some(predicate);
    }

    public mustInsert = (item: T): void => {
        if (this._itemMap.has(item.id)) {
            throw new Error(`Item with id ${item.id} already exists in collection`);
        }
        this._itemMap.set(item.id, item);
    }

    public maybeInsert = (item: T): void => {
        if (this._itemMap.has(item.id)) {
            return;
        }
        this._itemMap.set(item.id, item);
    }

    public upsert = (item: T): void => {
        this._itemMap.set(item.id, item);
    }
}